import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { TFleetCapabilities, TUserPermission } from '@/contexts/user-authorizations/UserPermissions';
import type { IRoleType } from '@/service/vpc/types/IUserAuthorizations';

import { isNativeWebview } from '@/config/nativeTokenStore';
import { FLEET_STORAGE_KEY, FleetAccountContext } from '@/contexts/fleet-account/FleetAccountContext';
import { useUserAuthorizations } from '@/hooks/useUserAuthorizations';
import { allUnitsQueryOptions, fleetAccountsQueryOptions } from '@/service/vpc/queryOptions';
import { chassis } from '@/utils';
import { includesNeededScope } from '@/utils/account';

export const FleetAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const accounts = instance.getActiveAccount();
  const { auths, loading: isLoadingAuths } = useUserAuthorizations();
  const hasInitialized = useRef(false);

  const {
    data: fleetAccounts,
    isSuccess,
    isLoading,
    isPending,
  } = useQuery({
    ...fleetAccountsQueryOptions(),
    enabled: !!accounts?.idToken || isNativeWebview(),
  });

  const { data: units, isPending: isLoadingUnits } = useQuery({
    ...allUnitsQueryOptions(),
    enabled: !!accounts?.idToken || isNativeWebview(),
  });

  // Initialize state from localStorage
  const [fleetAccountId, setFleetAccountIdState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(FLEET_STORAGE_KEY);
      return stored;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  });

  // Set the selected fleet account ID
  const setFleetAccountId = (id: string | null) => {
    setFleetAccountIdState(id);
    // Immediately update localStorage to ensure sync
    try {
      if (id !== null) {
        localStorage.setItem(FLEET_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(FLEET_STORAGE_KEY);
      }
      // Dispatch custom event to notify App.tsx
      window.dispatchEvent(new CustomEvent('fleetAccountChanged', { detail: id }));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  };

  // Clear the selected fleet account ID
  const clearFleetAccountId = useCallback(() => {
    setFleetAccountIdState(null);
    localStorage.removeItem(FLEET_STORAGE_KEY);
  }, []);

  const resolvedFleetAccountId = useMemo(() => {
    if (!fleetAccounts) {
      return fleetAccountId;
    }

    if (fleetAccounts.length === 1) {
      return fleetAccounts[0].fleetAccountId;
    }

    if (!fleetAccountId) {
      return null;
    }

    return fleetAccounts.some((fa) => fa.fleetAccountId === fleetAccountId) ? fleetAccountId : null;
  }, [fleetAccounts, fleetAccountId]);

  const selectedFleetAccount = useMemo(
    () => fleetAccounts?.find((fa) => fa.fleetAccountId === resolvedFleetAccountId) || null,
    [fleetAccounts, resolvedFleetAccountId]
  );

  const fleetAccountUnitsIds = useCallback(() => {
    if (!units) {
      return [];
    }

    const authFleet = resolvedFleetAccountId ? auths?.fbbRolesAndUnitCapabilities?.[resolvedFleetAccountId] : undefined;

    const unitIds = [...(authFleet?.fleetAccountUnits ?? [])];
    const unitIdSet = new Set(unitIds);

    // Include units confirmed by auth service, plus any claiming (pending) units
    const result =
      unitIds.length > 0
        ? units.filter((u) => u.endCustomerUnitId != null && (unitIdSet.has(u.endCustomerUnitId) || u.claiming))
        : resolvedFleetAccountId
          ? units.filter((u) => u.claiming) // Fleet selected but no auth units — still include claiming ones
          : units.filter((u) => u.endCustomerUnitId != null);

    return result.map((u) => u.endCustomerUnitId);
  }, [units, auths, resolvedFleetAccountId]);

  const isDigitalAccount = useMemo(() => accounts?.idTokenClaims?.userType === 'enduser', [accounts]);

  const fleetVerifiedUnits = useMemo(
    () =>
      units
        ?.filter((u) => !u.claiming)
        ?.filter(
          (u) =>
            chassis.isIps40(u) ||
            (u.telemetryDeviceInfo?.some((device) => device.device === 'TGW') &&
              u.serviceStatuses?.some((service) => service.status === 'Active'))
        )
        ?.filter((u) => fleetAccountUnitsIds().includes(u.endCustomerUnitId)) || [],
    [units, fleetAccountUnitsIds]
  );

  const fleetAccountUnits = useMemo(
    () => units?.filter((u) => fleetAccountUnitsIds().includes(u.endCustomerUnitId)) || [],
    [units, fleetAccountUnitsIds]
  );

  const isMaximumClaimedReached = useMemo(() => {
    if (!fleetAccounts || !resolvedFleetAccountId) {
      return false;
    }
    return fleetAccountUnits.reduce((count, unit) => (unit.claiming ? count + 1 : count), 0) >= 10;
  }, [fleetAccounts, resolvedFleetAccountId, fleetAccountUnits]);

  const isMarineAccount = useMemo(() => {
    return fleetAccountUnits.every((unit) => unit.mainSegment === 'Marine');
  }, [fleetAccountUnits]);

  const isIndustrialAccount = useMemo(() => {
    return fleetAccountUnits.every((unit) => unit.mainSegment === 'Industrial');
  }, [fleetAccountUnits]);

  const userHasPermission = useCallback(
    (permission: TUserPermission) => !!auths?.userPermissions?.some((p) => includesNeededScope(p, permission)),
    [auths]
  );

  const userHasRole = useCallback(
    (role: IRoleType[], fleetAccountId: string) => {
      // ifno fleetAccountId provide use the 1st fleet account
      const targetAccountId = fleetAccountId || Object.keys(auths?.fbbRolesAndUnitCapabilities || {})[0];

      return !!auths?.fbbRolesAndUnitCapabilities?.[targetAccountId]?.fleetAccountRoles?.some((r) =>
        role.includes(r.role.roleType)
      );
    },
    [auths]
  );

  const userHasCapability = useCallback(
    (capability: TFleetCapabilities, unitId: number) => {
      if (!unitId) {
        throw new Error('unitId is required to check capabilities. Please provide a valid unit ID.');
      }

      if (!auths?.fbbRolesAndUnitCapabilities) {
        return false;
      }

      // Collect capabilities from all fleet accounts that contain this unit
      const unitCapabilities = new Set<string>();

      Object.values(auths.fbbRolesAndUnitCapabilities).forEach((fleetAccount) => {
        const allUnits = [...new Set([...fleetAccount.fleetAccountUnits])];

        // Check if this fleet account contains the unit
        if (allUnits.includes(unitId)) {
          // Collect all roles from this fleet account
          const allRoles = [...fleetAccount.fleetAccountRoles];

          // Extract capabilities from all roles
          allRoles.forEach((role) => {
            role.role.capabilities.forEach((cap) => {
              if (cap !== 'None') {
                unitCapabilities.add(cap);
              }
            });
          });
        }
      });

      return unitCapabilities.has(capability);
    },
    [auths]
  );

  useEffect(() => {
    // Only run initialization once when data is loaded
    if (!isLoading && isSuccess && fleetAccounts && !hasInitialized.current) {
      hasInitialized.current = true;

      // Check if /invite/${invitationId} route we dont want to redirect then
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/invite/')) {
        return;
      }

      if (fleetAccounts.length > 1) {
        if (!resolvedFleetAccountId) {
          // No valid fleet account is selected for multi-account users, redirect to picker.
          localStorage.removeItem(FLEET_STORAGE_KEY);
          window.dispatchEvent(new CustomEvent('fleetAccountChanged', { detail: null }));
          navigate({ to: '/select-fleet', replace: true, search: (prev) => prev });
        } else {
          // Persist a valid selection in storage if missing or stale.
          const storedFleetAccountId = localStorage.getItem(FLEET_STORAGE_KEY);
          if (storedFleetAccountId !== resolvedFleetAccountId) {
            localStorage.setItem(FLEET_STORAGE_KEY, resolvedFleetAccountId);
            window.dispatchEvent(new CustomEvent('fleetAccountChanged', { detail: resolvedFleetAccountId }));
          }
        }
      } else if (fleetAccounts.length === 1) {
        // Auto-select single-account users via storage/event sync.
        const singleAccountId = fleetAccounts[0].fleetAccountId;
        const storedFleetAccountId = localStorage.getItem(FLEET_STORAGE_KEY);
        if (storedFleetAccountId !== singleAccountId) {
          localStorage.setItem(FLEET_STORAGE_KEY, singleAccountId);
          window.dispatchEvent(new CustomEvent('fleetAccountChanged', { detail: singleAccountId }));
        }
      }
    }
  }, [isLoading, isSuccess, fleetAccounts, resolvedFleetAccountId, navigate]);

  const value = {
    isLoading: isLoading || isPending || isLoadingUnits || isLoadingAuths,
    isDigitalAccount: isDigitalAccount || false,
    fleetAccounts,
    fleetAccountId: resolvedFleetAccountId,
    selectedFleetAccount,
    fleetVerifiedUnits,
    fleetAccountUnits,
    isMaximumClaimedReached,
    isMarineAccount,
    isIndustrialAccount,
    userHasPermission,
    userHasRole,
    userHasCapability,
    setFleetAccountId,
    clearFleetAccountId,
  };

  return <FleetAccountContext.Provider value={value}>{children}</FleetAccountContext.Provider>;
};
